import React, { useState, useEffect } from 'react';
import { Star, StarOff } from 'lucide-react';
import { Button } from '../ui/button';
import { adminStoresAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const FeaturedManagement = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const featuredCount = stores.filter(s => s.isFeatured).length;

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      const response = await adminStoresAPI.getAll();
      setStores(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar las tiendas",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleFeatured = async (storeId, currentFeatured) => {
    const store = stores.find(s => s.id === storeId);
    
    // Check if trying to feature when already at limit
    if (!currentFeatured && featuredCount >= 5) {
      toast({
        title: "Límite alcanzado",
        description: "Solo puedes tener 5 tiendas destacadas. Remueve una primero.",
        variant: "destructive"
      });
      return;
    }

    try {
      await adminStoresAPI.toggleFeatured(storeId, !currentFeatured);
      setStores(stores.map(s => 
        s.id === storeId ? { ...s, isFeatured: !currentFeatured } : s
      ));
      
      toast({
        title: currentFeatured ? "Removida de destacadas" : "Añadida a destacadas",
        description: `${store.name} ha sido ${currentFeatured ? 'removida de' : 'añadida a'} destacadas`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo actualizar",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <p className="text-white">Cargando...</p>
      </div>
    );
  }

  const participatingStores = stores.filter(s => s.isParticipating);
  const featuredStores = participatingStores.filter(s => s.isFeatured);
  const nonFeaturedStores = participatingStores.filter(s => !s.isFeatured);

  return (
    <>
      <div className="p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Tiendas destacadas</h2>
            <p className="text-[#A2A2A2]">Selecciona hasta 5 tiendas para destacar ({featuredCount}/5)</p>
          </div>

          {/* Featured Stores */}
          {featuredStores.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-white">Destacadas actuales</h3>
              {featuredStores.map((store) => (
                <div
                  key={store.id}
                  className="bg-black border border-white/20 rounded-3xl p-6"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Star className="w-5 h-5 text-white fill-white" />
                        <h3 className="text-xl font-semibold text-white">{store.name}</h3>
                      </div>
                      <p className="text-[#A2A2A2] text-sm mb-1">{store.category}</p>
                      <p className="text-white">{store.benefit}</p>
                    </div>

                    <Button
                      onClick={() => toggleFeatured(store.id, store.isFeatured)}
                      variant="outline"
                      className="border-white/10 text-white bg-transparent hover:bg-white/10 rounded-full"
                    >
                      <StarOff className="w-4 h-4 mr-2" />
                      Remover
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Available Stores */}
          {nonFeaturedStores.length > 0 && (
            <div className="space-y-3 mt-8">
              <h3 className="text-xl font-semibold text-white">Tiendas disponibles</h3>
              {nonFeaturedStores.map((store) => (
                <div
                  key={store.id}
                  className="bg-black border border-white/10 rounded-3xl p-6 hover:border-white/20 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-2">{store.name}</h3>
                      <p className="text-[#A2A2A2] text-sm mb-1">{store.category}</p>
                      <p className="text-white">{store.benefit}</p>
                    </div>

                    <Button
                      onClick={() => toggleFeatured(store.id, store.isFeatured)}
                      disabled={featuredCount >= 5}
                      className="bg-white text-black hover:bg-white/90 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-105 active:scale-95"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Destacar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Toaster />
    </>
  );
};

export default FeaturedManagement;
